import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaShareAlt } from "react-icons/fa";
import Header from "../../components/NavBar";
import Footer from "../../components/Footer";
import "./style.css";
import defaultImage from "../../assets/defaultImagecompressed.jpg";

const ArticlePage = () => {
  const { ArticleId, titleSlug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Function to create a URL-friendly slug
  const createSlug = (title) => {
    if (!title) return "";
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
      .trim()
      .replace(/[\s_-]+/g, "-") // Replace spaces and underscores with a single hyphen
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  };

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await fetch(
          `https://homchang.in/get_article.php?ArticleId=${ArticleId}`
        );

        const data = await response.json();

        if (data.success) {
          setArticle(data.data);

          // Create the correct slug from the article title
          const correctSlug = createSlug(data.data.Title);

          // If no slug is provided or the current slug doesn't match the correct slug
          if (!titleSlug || titleSlug !== correctSlug) {
            // Navigate to the correct URL with the slug
            navigate(`/articles/${ArticleId}/${correctSlug}`, {
              replace: true,
            });
          }
        } else {
          setError(data.message);
        }
      } catch (error) {
        console.error("Error fetching article:", error);
        setError("An error occurred while fetching the article.");
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [ArticleId, titleSlug, navigate]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Loading...</p>
      </div>
    );

  if (error)
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-red-500">{error}</p>
      </div>
    );

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: article.Title,
          text: `Check out this article: ${article.Title}`,
          url: window.location.href,
        })
        .then(() => console.log("Article shared successfully"))
        .catch((err) => console.error("Error sharing article:", err));
    } else {
      alert("Sharing is not supported in this browser.");
    }
  };

  // Check if the ImageURL is for the default image
  const isDefaultImageUrl = (url) => {
    if (!url) return false;
    return url.includes("defaultImagecompressed") || url === defaultImage;
  };

  // Check if we should display an image
  const shouldDisplayImage = () => {
    // If ImageURL exists and is not empty, show it
    if (article.ImageURL && article.ImageURL.trim() !== "") {
      return true;  //Change this to true to allow image
    }
    return false;
  };

  // Get the image URL to display
  const getImageUrl = () => {
    // If there's no ImageURL or it's empty, use the imported default image
    if (!article.ImageURL || article.ImageURL.trim() === "") {
      return defaultImage;
    }

    // If ImageURL is the default image path, use the imported default image
    // This ensures we're using the actual file, not a potentially stale URL
    if (isDefaultImageUrl(article.ImageURL)) {
      return defaultImage;
    }

    // Otherwise use the article's ImageURL
    return article.ImageURL;
  };

  return (
    <div className="bg-gray-200">
      <div className="bg-black">
        <Header />
      </div>
      <div className="main-body justify-center">
        <div className="larger-section">
          <div className="mx-auto p-6">
            {/* Article Category */}
            <p className="text-sm text-gray-600 mb-4 flex border-b border-black pb-2">
              {article.CategoryName || "Uncategorized"}
            </p>
            {/* Article Title */}
            <h1 className="text-4xl font-bold text-start text-gray-800">
              {article.Title}
            </h1>
            {/* Article Metadata */}
            <div className="text-sm text-gray-600 mb-4 flex justify-between items-center border-b border-black pb-2">
              <p>
                <span className="font-semibold"> By:</span>{" "}
                {article.AuthorName || "Unknown"}
              </p>
              <p>
                <span className="font-semibold"> Updated On : </span>
                {new Date(article.PublishedDate).toLocaleDateString()}
              </p>
            </div>
            {/* Share Button */}
            <div className="mb-2 flex justify-end items-center">
              <button
                onClick={handleShare}
                className="flex items-center text-black bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors duration-300"
              >
                <FaShareAlt className="w-5 h-5" />
              </button>
            </div>
            {/* Conditionally Render Image Section */}
            {shouldDisplayImage() && (
              <div className="mb-4">
                <img
                  src={getImageUrl()}
                  alt={article.AltText || "Article image"}
                  className="w-full h-120 object-fit"
                  onError={(e) => {
                    console.log("Image error, using default");
                    e.target.src = defaultImage;
                  }}
                />
              </div>
            )}
            {/* Article Content */}
            <div
              className="prose-body"
              dangerouslySetInnerHTML={{ __html: article.Content }}
            />
          </div>
        </div>
        <div className="smaller-section">{/* Empty section */}</div>
      </div>
      <Footer />
    </div>
  );
};

export default ArticlePage;
